import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, brl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search as SearchIcon, Filter, Loader2, MapPin, Gavel, Coins, Heart, Bell } from "lucide-react";
import { toast } from "sonner";

export default function Search() {
  const { user, updateCredits } = useAuth();
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [types, setTypes] = useState([]);
  const [uf, setUf] = useState("all");
  const [vType, setVType] = useState("all");
  const [only25, setOnly25] = useState(false);
  const [brand, setBrand] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sortBy, setSortBy] = useState("score");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [favIds, setFavIds] = useState(new Set());

  useEffect(() => {
    api.get("/meta/states").then((r) => setStates(r.data.states));
    api.get("/meta/vehicle-types").then((r) => setTypes(r.data.types));
    api.get("/favorites").then((r) => setFavIds(new Set(r.data.ids || []))).catch(() => {});
  }, []);

  const toggleFav = async (vid, e) => {
    e.stopPropagation();
    try {
      if (favIds.has(vid)) {
        await api.delete(`/favorites/${vid}`);
        const nf = new Set(favIds); nf.delete(vid); setFavIds(nf);
      } else {
        await api.post("/favorites", { vehicle_id: vid });
        setFavIds(new Set([...favIds, vid]));
      }
    } catch { toast.error("Falha ao atualizar favoritos"); }
  };

  const saveAsAlert = async () => {
    try {
      await api.post("/alerts", {
        name: `Alerta ${new Date().toLocaleDateString("pt-BR")}`,
        vehicle_type: vType, uf,
        only_25_30: only25,
        brand: brand || null,
        min_price: minPrice ? Number(minPrice) : null,
        max_price: maxPrice ? Number(maxPrice) : null,
        min_score: 70,
      });
      toast.success("Filtro salvo como alerta");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const runSearch = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/search", {
        uf, vehicle_type: vType, only_25_30: only25,
        brand: brand || null,
        min_price: minPrice ? Number(minPrice) : null,
        max_price: maxPrice ? Number(maxPrice) : null,
        sort_by: sortBy,
      });
      setResults(data);
      updateCredits(data.credits_remaining);
      toast.success(`${data.total} lotes encontrados · 1 crédito consumido`);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(msg);
      if (e.response?.status === 402) navigate("/store");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">Pesquisa de Leilões</h1>
            <p className="text-slate-400 text-sm mt-1">1 pesquisa = 1 crédito. Saldo: <span className="font-mono text-emerald-400">{user?.credits ?? 0}</span></p>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 p-5 rounded-xl bg-slate-900/60 border border-slate-800" data-testid="search-filters">
          <div className="flex items-center gap-2 mb-4 text-slate-300"><Filter className="w-4 h-4" /><span className="text-sm font-semibold">Filtros</span></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Tipo</Label>
              <Select value={vType} onValueChange={setVType}>
                <SelectTrigger className="mt-1.5 bg-slate-950 border-slate-800 h-11" data-testid="filter-vehicle-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Estado</Label>
              <Select value={uf} onValueChange={setUf}>
                <SelectTrigger className="mt-1.5 bg-slate-950 border-slate-800 h-11" data-testid="filter-uf"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 max-h-72">
                  <SelectItem value="all">Todos os estados</SelectItem>
                  {states.map((s) => <SelectItem key={s.uf} value={s.uf}>{s.uf} — {s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Marca / Modelo</Label>
              <Input placeholder="Ex: Corolla" value={brand} onChange={(e) => setBrand(e.target.value)}
                className="mt-1.5 bg-slate-950 border-slate-800 h-11" data-testid="filter-brand" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Ordenar por</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="mt-1.5 bg-slate-950 border-slate-800 h-11" data-testid="filter-sort"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="score">Melhor oportunidade</SelectItem>
                  <SelectItem value="savings">Maior desconto FIPE</SelectItem>
                  <SelectItem value="price_asc">Menor preço</SelectItem>
                  <SelectItem value="price_desc">Maior preço</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Preço mínimo</Label>
              <Input type="number" placeholder="R$" value={minPrice} onChange={(e) => setMinPrice(e.target.value)}
                className="mt-1.5 bg-slate-950 border-slate-800 h-11" data-testid="filter-min-price" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Preço máximo</Label>
              <Input type="number" placeholder="R$" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)}
                className="mt-1.5 bg-slate-950 border-slate-800 h-11" data-testid="filter-max-price" />
            </div>
            <div className="flex items-center gap-3 col-span-1 sm:col-span-2 lg:col-span-2 mt-2">
              <Switch checked={only25} onCheckedChange={setOnly25} id="only25" data-testid="filter-only-25-30" />
              <Label htmlFor="only25" className="text-slate-200 cursor-pointer flex items-center gap-2">
                <span className="badge-parcel"><Gavel className="w-3 h-3" />25% + 30x</span>
                Apenas modalidade parcelada
              </Label>
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button onClick={saveAsAlert} variant="outline" className="border-emerald-700/50 text-emerald-300 hover:bg-emerald-500/10 h-11" data-testid="save-alert-btn">
              <Bell className="w-4 h-4 mr-2" />Salvar como alerta
            </Button>
            <Button onClick={runSearch} disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold h-11 px-6" data-testid="search-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <SearchIcon className="w-4 h-4 mr-2" />}
              Pesquisar {user?.is_pro ? "(ilimitado)" : "(-1 crédito)"}
            </Button>
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-400" data-testid="results-count">
                <span className="font-mono text-white font-bold">{results.total}</span> lotes encontrados
              </div>
              <div className="text-xs font-mono text-amber-300 flex items-center gap-1"><Coins className="w-3 h-3" />{results.credits_remaining} créditos restantes</div>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.results.map((v) => (
                <VehicleCard v={v} key={v.id} onClick={() => navigate(`/vehicle/${v.id}`)} isFav={favIds.has(v.id)} onToggleFav={(e) => toggleFav(v.id, e)} />
              ))}
            </div>
            {results.results.length === 0 && (
              <div className="mt-10 p-8 rounded-xl border border-dashed border-slate-800 text-center text-slate-400">
                Nenhum lote encontrado com esses filtros. Tente ampliar sua busca.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function VehicleCard({ v, onClick, isFav, onToggleFav }) {
  return (
    <div onClick={onClick} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") onClick(); }} className="cursor-pointer text-left bg-slate-900/70 border border-slate-800 rounded-xl overflow-hidden card-hover relative" data-testid={`vehicle-card-${v.id}`}>
      <div className="aspect-video bg-slate-800 relative">
        <img src={v.image} alt={v.model} className="w-full h-full object-cover" />
        <div className="absolute top-2 left-2 deal-score">{v.analysis.score}/100</div>
        {v.payment_25_30 && <div className="absolute top-2 right-12 badge-parcel"><Gavel className="w-3 h-3" />25% + 30x</div>}
        <button type="button" onClick={onToggleFav} className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isFav ? "bg-red-500/25 border border-red-500/50" : "bg-black/50 border border-slate-700 hover:bg-red-500/20"}`} data-testid={`fav-btn-${v.id}`}>
          <Heart className={`w-4 h-4 ${isFav ? "text-red-400 fill-red-400" : "text-slate-300"}`} />
        </button>
        <div className="absolute bottom-2 left-2 text-[10px] font-mono uppercase text-slate-300 bg-black/50 px-2 py-0.5 rounded flex items-center gap-1"><MapPin className="w-3 h-3" />{v.uf}</div>
      </div>
      <div className="p-4">
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-white truncate">{v.brand} {v.model}</div>
            <div className="text-xs text-slate-400">{v.year} · {v.km.toLocaleString("pt-BR")} km</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-emerald-400">{v.analysis.rating}</div>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Lance mínimo</div>
            <div className="font-mono text-emerald-400 font-bold">{brl(v.min_bid)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">FIPE</div>
            <div className="font-mono text-slate-400 text-sm line-through">{brl(v.fipe_value)}</div>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-800 flex items-center justify-between">
          <div className="text-xs text-slate-400 truncate">{v.auctioneer_name}</div>
          <div className="text-xs font-mono text-emerald-300">−{v.analysis.savings_pct}%</div>
        </div>
      </div>
    </div>
  );
}
